import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, FolderOpen, Users, MapPin, File } from "@/lib/icons";

export default function Connect() {
  const [collections, setCollections] = useState([]);
  const [people, setPeople] = useState([]);
  const [places, setPlaces] = useState([]);

  const load = () => {
    api.get("/collections").then((r) => setCollections(r.data.collections)).catch(() => {});
    api.get("/people").then((r) => setPeople(r.data.people)).catch(() => {});
    api.get("/places").then((r) => setPlaces(r.data.places)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader eyebrow="Connect" color="#F97316" title="Connect"
        subtitle="Link people, places, projects, and stories." />

      <Tabs defaultValue="collections">
        <TabsList className="mb-5">
          <TabsTrigger value="collections" data-testid="tab-collections">Collections & Projects</TabsTrigger>
          <TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>
          <TabsTrigger value="places" data-testid="tab-places">Places</TabsTrigger>
        </TabsList>

        <TabsContent value="collections">
          <div className="flex justify-end mb-4"><CollectionDialog onDone={load} /></div>
          {collections.length === 0 ? <EmptyState icon={FolderOpen} title="No collections yet" hint="Group related captures into collections, or create a project to add tasks and status." /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="collections-grid">
              {collections.map((c) => (
                <Link key={c.id} to={`/collections/${c.id}`} className="dl-card p-4" data-testid={`collection-card-${c.id}`}>
                  <span className="h-10 w-10 rounded-lg grid place-items-center mb-3" style={{ background: c.color + "22", color: c.color }}><File size={18} /></span>
                  <div className="text-sm font-semibold text-white truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{c.item_count} items · {c.kind}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="people">
          <div className="flex justify-end mb-4"><PersonDialog onDone={load} /></div>
          {people.length === 0 ? <EmptyState icon={Users} title="No people yet" hint="Create reusable people records you can link to captures and collections." /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {people.map((p) => (
                <div key={p.id} className="dl-card p-4 flex items-center gap-3" data-testid={`person-card-${p.id}`}>
                  <span className="h-10 w-10 rounded-full grid place-items-center text-white font-semibold" style={{ background: "#EF4444" }}>{p.name.slice(0, 1)}</span>
                  <div className="min-w-0"><div className="text-sm font-semibold text-white truncate">{p.name}</div><div className="text-[11px] text-slate-500 truncate">{p.role || "—"}</div></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="places">
          <div className="flex justify-end mb-4"><PlaceDialog onDone={load} /></div>
          {places.length === 0 ? <EmptyState icon={MapPin} title="No places yet" hint="Save marinas, inlets, and spots. Coordinates are optional." /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {places.map((p) => (
                <div key={p.id} className="dl-card p-4" data-testid={`place-card-${p.id}`}>
                  <span className="h-10 w-10 rounded-lg grid place-items-center mb-3" style={{ background: "#14B8A622", color: "#14B8A6" }}><MapPin size={18} /></span>
                  <div className="text-sm font-semibold text-white truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{p.address || (p.lat ? `${p.lat.toFixed(3)}, ${p.lng.toFixed(3)}` : "—")}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CollectionDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("collection");
  const save = async () => {
    if (!name.trim()) return;
    await api.post("/collections", { name, kind });
    toast.success("Created"); setName(""); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="new-collection-btn" style={{ background: "#2563EB" }}><Plus size={16} /> New</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New collection or project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="collection-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="collection-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="collection">Collection (organize material)</SelectItem><SelectItem value="project">Project (tasks + status)</SelectItem></SelectContent>
            </Select></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-collection-btn" style={{ background: "#2563EB" }}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [role, setRole] = useState("");
  const save = async () => { if (!name.trim()) return; await api.post("/people", { name, role }); toast.success("Added"); setName(""); setRole(""); setOpen(false); onDone(); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="new-person-btn" style={{ background: "#2563EB" }}><Plus size={16} /> New person</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New person</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="person-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Role (optional)</Label><Input value={role} onChange={(e) => setRole(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-person-btn" style={{ background: "#2563EB" }}>Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlaceDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [address, setAddress] = useState("");
  const save = async () => { if (!name.trim()) return; await api.post("/places", { name, address }); toast.success("Added"); setName(""); setAddress(""); setOpen(false); onDone(); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="new-place-btn" style={{ background: "#2563EB" }}><Plus size={16} /> New place</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New place</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="place-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Address (optional)</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-place-btn" style={{ background: "#2563EB" }}>Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
